import {useCallback, useMemo} from "react";

import type {Listing} from "../../models/listing";
import type {ListingGroup} from "../../models/listingGroup";
import type {Lock} from "../../models/lock";
import {CloseIcon, memoWithType} from "../../react";
import ButtonIconTooltip from "../ButtonIconTooltip";
import ListingChannelsLogo from "../ListingChannelsLogo";
import {type OptionType} from "../Select";
import SelectGeneric from "../SelectGeneric";
import {HStack} from "../ui/hstack";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

type Objects = Listing | ListingGroup | Lock;

type Props<T extends Objects> = {
    list: T[];
    disabled?: boolean;
    testID?: string;
} & StrictUnion<
    | {
          mode: "multiple";
          value?: string[];
          onSelect: (value: string[]) => void;
      }
    | {
          mode: "single";
          value?: string;
          onSelect: (value: string) => void;
      }
>;

function SelectObject<T extends Objects>({
    testID,
    mode,
    list,
    value,
    disabled,
    onSelect
}: Props<T>) {
    const buildOptions = useCallback(
        (excludedID?: string | string[]) => {
            return list.reduce<OptionType<string>[]>((acc, elem) => {
                if (excludedID) {
                    const exclusion = typeof excludedID === "string" ? [excludedID] : excludedID;
                    if (!exclusion.includes(elem._id)) {
                        acc.push({
                            label: elem.name,
                            value: elem._id
                        });
                    }
                } else {
                    acc.push({
                        label: elem.name,
                        value: elem._id
                    });
                }
                return acc;
            }, []);
        },
        [list]
    );

    const options = useMemo<OptionType<string>[]>(() => buildOptions(), [buildOptions]);
    const selectedValues = useMemo(() => {
        if (mode === "multiple" && value) {
            return list.filter(item => value.includes(item._id));
        }
        return [];
    }, [mode, list, value]);

    return (
        <VStack space="sm">
            {mode === "multiple" ? (
                <SelectGeneric
                    isMulti
                    isDisabled={disabled}
                    maxMenuHeight={200}
                    options={options}
                    testID={testID}
                    value={value}
                    onChange={onSelect}
                />
            ) : (
                <SelectGeneric
                    isDisabled={disabled}
                    maxMenuHeight={200}
                    options={options}
                    testID={testID}
                    value={value}
                    onChange={onSelect}
                />
            )}
            {mode === "multiple" && selectedValues.length !== 0 && (
                <HStack className="flex flex-wrap" space="sm">
                    {selectedValues.map(elem => (
                        <HStack
                            key={elem._id}
                            className="bg-blue-100 max-w-full items-center rounded-md p-1 mb-1"
                            space="sm"
                        >
                            {"airbnbName" in elem ? <ListingChannelsLogo listing={elem} /> : null}
                            <Text isTruncated className="shrink grow" variant="sm">
                                {elem.name}
                            </Text>
                            <ButtonIconTooltip
                                icon={CloseIcon}
                                tooltip="Remove"
                                onPress={() => {
                                    if (value) {
                                        const next = value.filter(item => item !== elem._id);
                                        onSelect(next);
                                    }
                                }}
                            />
                        </HStack>
                    ))}
                </HStack>
            )}
        </VStack>
    );
}

export default memoWithType(SelectObject);
